import { IGenEq, IGenSingle, GenData, QEq, QSingle, QMulti, IGenMulti, eqToSingle } from '@educraft/base';
import { NAMES_DATA } from './namesDataset';

export class SimpleGenEq implements IGenEq {
  constructor(
    private callback: (data: GenData) => QEq,
    public name: string
  ) {}
  generate(data: GenData): QEq {
    return this.callback(data);
  }

  static combine(items: SimpleGenEq[]): SimpleGenEq {
    const dictionary = new Map<SimpleGenEq, number>(items.map((item) => [item, 1]));
    return this.combineD(dictionary);
  }

  static combineD(dictionary: Map<SimpleGenEq, number>): SimpleGenEq {
    let max = 0;
    dictionary.forEach((v) => (max += v));
    const name = Array.from(dictionary.keys())
      .map((k) => k.name)
      .join(', ');
    return new SimpleGenEq((genData) => {
      const rnd = genData.random.next(max);
      let count = 0;
      for (const [key, value] of dictionary) {
        if (rnd < count + value) return key.generate(genData);
        count += value;
      }
      throw new Error('Selection failed');
    }, name);
  }
}

export class SimpleGenSingle implements IGenSingle {
  constructor(
    private callback: (data: GenData) => QSingle,
    public name: string
  ) {}
  generate(data: GenData): QSingle {
    return this.callback(data);
  }

  static combine(items: (IGenSingle | IGenEq)[]): IGenSingle {
    const normalized: IGenSingle[] = items.map((item) => {
      // Check if it's IGenEq by looking for common properties or using duck typing
      // In our case, we can check if it returns QEq or QSingle, but it's better
      // to rely on the fact that we know which is which.
      // However, SimpleGenSingle and SimpleGenEq are both present here.
      if (item instanceof SimpleGenEq) {
        return eqToSingle(item);
      }
      // Fallback duck typing: if generate returns something with 'text' and 'result', it's Eq
      // But we can't easily check that without calling it.
      // For now, let's assume if it's not IGenSingle, we try eqToSingle.
      return item as IGenSingle;
    });

    const name = items.map((i: any) => i.name || 'Gen').join(', ');

    return new SimpleGenSingle((genData) => {
      const idx = genData.random.next(normalized.length);
      const gen = normalized[idx];
      // If we didn't normalize correctly before, we might need a runtime check
      const res = gen.generate(genData);
      if ('text' in res && 'result' in res) {
        return { texts: [(res as any).text], answer: String((res as any).result) };
      }
      return res as QSingle;
    }, name);
  }
}

export const Numerals = {
  SuperScriptData: '⁰¹²³⁴⁵⁶⁷⁸⁹⁻⁺',
  SubScriptData: '₀₁₂₃₄₅₆₇₈₉₋₊',

  script(data: string, value: number): string {
    let minus = '';
    if (value < 0) {
      minus = data[10];
      value = -value;
    }

    const str = value.toString();
    let result = minus;
    for (const char of str) {
      result += data[parseInt(char)];
    }
    return result;
  },

  superScript(value: number): string {
    return this.script(this.SuperScriptData, value);
  },

  subScript(value: number): string {
    return this.script(this.SubScriptData, value);
  },

  strAdd(n: number): string {
    return n >= 0 ? `+ ${n}` : `− ${-n}`;
  },

  strNum(n: number): string {
    return n >= 0 ? `${n}` : `−${-n}`;
  },

  toRomanString(number: number): string {
    const romanMap: [number, string][] = [
      [1000, 'M'],
      [900, 'CM'],
      [500, 'D'],
      [400, 'CD'],
      [100, 'C'],
      [90, 'XC'],
      [50, 'L'],
      [40, 'XL'],
      [10, 'X'],
      [9, 'IX'],
      [5, 'V'],
      [4, 'IV'],
      [1, 'I'],
    ];
    let result = '';
    for (const [val, str] of romanMap) {
      while (number >= val) {
        result += str;
        number -= val;
      }
    }
    return result;
  },

  strOne(n: number): string {
    if (n === 1) return '';
    if (n === -1) return '−';
    return this.strNum(n);
  },

  strAddOne(n: number, first: boolean): string {
    if (n === 1 && first) return '';
    if (n === 1) return ' + ';
    if (n === -1) return ' − ';
    if (first) return this.strNum(n);
    return this.strAdd(n);
  },

  toEnglishString(number: number): string {
    if (number === 0) return 'zero';
    if (number < 0) return `minus ${  this.toEnglishString(Math.abs(number))}`;

    let words = '';
    const units = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
    const teens = [
      'ten',
      'eleven',
      'twelve',
      'thirteen',
      'fourteen',
      'fifteen',
      'sixteen',
      'seventeen',
      'eighteen',
      'nineteen',
    ];
    const tens = [
      '',
      'ten',
      'twenty',
      'thirty',
      'forty',
      'fifty',
      'sixty',
      'seventy',
      'eighty',
      'ninety',
    ];

    if (Math.floor(number / 1000000) > 0) {
      words += `${this.toEnglishString(Math.floor(number / 1000000))  } million `;
      number %= 1000000;
    }

    if (Math.floor(number / 1000) > 0) {
      words += `${this.toEnglishString(Math.floor(number / 1000))  } thousand `;
      number %= 1000;
    }

    if (Math.floor(number / 100) > 0) {
      words += `${this.toEnglishString(Math.floor(number / 100))  } hundred `;
      number %= 100;
    }

    if (number > 0) {
      if (words.length > 0) words += 'and ';
      if (number < 10) words += units[number];
      else if (number < 20) words += teens[number - 10];
      else {
        words += tens[Math.floor(number / 10)];
        if (number % 10 > 0) words += `-${  units[number % 10]}`;
      }
    }

    return words.trim();
  },
};

export const CalcUtil = {
  binomial: (n: number, k: number): number => {
    if (k < 0 || k > n) return 0;
    if (k > n - k) k = n - k;
    let result = 1;
    for (let i = 1; i <= k; i++) {
      result *= n - (k - i);
      result /= i;
    }
    return result;
  },
  factorial: (n: number): number => {
    if (n < 0) throw new Error('n must be non-negative');
    if (n === 0 || n === 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) result *= i;
    return result;
  },
  gcd: (a: number, b: number): number => {
    while (b !== 0) {
      const temp = b;
      b = a % b;
      a = temp;
    }
    return Math.abs(a);
  },
  variation: (n: number, k: number): number => {
    if (k < 0 || k > n) throw new Error('k must be between 0 and n');
    return CalcUtil.factorial(n) / CalcUtil.factorial(n - k);
  },
};

export class Fraction {
  constructor(
    public readonly a: number,
    public readonly b = 1
  ) {}

  get A(): number {
    return this.a;
  }
  get B(): number {
    return this.b;
  }

  toString(): string {
    return `${Numerals.superScript(this.a)}/${Numerals.subScript(this.b)}`;
  }

  toNumber(): string {
    const simple = this.simplify();
    if (simple.b === 1) return simple.a.toString();
    return simple.toString();
  }

  simplify(): Fraction {
    const d = CalcUtil.gcd(this.a, this.b);
    const sign = this.b < 0 ? -1 : 1;
    return new Fraction((sign * this.a) / d, Math.abs(this.b) / d);
  }

  compareTo(other: Fraction): number {
    return this.a * other.b - other.a * this.b;
  }

  equals(other: Fraction): boolean {
    const o1 = this.simplify();
    const o2 = other.simplify();
    return o1.a === o2.a && o1.b === o2.b;
  }

  static get Zero(): Fraction {
    return new Fraction(0, 1);
  }
  static get One(): Fraction {
    return new Fraction(1, 1);
  }

  static add(x: Fraction, y: Fraction): Fraction {
    if (x.b === y.b) return new Fraction(x.a + y.a, x.b);
    const comm = (x.b * y.b) / CalcUtil.gcd(x.b, y.b);
    return new Fraction((x.a * comm) / x.b + (y.a * comm) / y.b, comm);
  }

  static subtract(x: Fraction, y: Fraction): Fraction {
    if (x.b === y.b) return new Fraction(x.a - y.a, x.b);
    const comm = (x.b * y.b) / CalcUtil.gcd(x.b, y.b);
    return new Fraction((x.a * comm) / x.b - (y.a * comm) / y.b, comm);
  }

  static multiply(x: Fraction, y: Fraction): Fraction {
    return new Fraction(x.a * y.a, x.b * y.b);
  }

  static divide(x: Fraction, y: Fraction): Fraction {
    if (y.a === 0) throw new Error('Divide by zero');
    return new Fraction(x.a * y.b, x.b * y.a);
  }

  static negate(x: Fraction): Fraction {
    return new Fraction(-x.a, x.b);
  }

  static increment(x: Fraction): Fraction {
    return new Fraction(x.a + x.b, x.b);
  }

  static decrement(x: Fraction): Fraction {
    return new Fraction(x.a - x.b, x.b);
  }

  static create(a: number, b: number): Fraction {
    return new Fraction(a, b);
  }
}

export const Common = {
  plus:
    (numbersGen: (r: any) => number[]) =>
    (data: GenData): QEq => {
      const nums = numbersGen(data.random);
      return {
        text: `${nums.join(' + ')  } =`,
        result: nums.reduce((a, b) => a + b, 0),
      };
    },

  minus:
    (numbersGen: (r: any) => number[]) =>
    (data: GenData): QEq => {
      const arr = numbersGen(data.random);
      if (arr.length !== 2) throw new Error('Minus requires 2 numbers');
      return {
        text: `${arr[0] + arr[1]} – ${arr[1]} =`,
        result: arr[0],
      };
    },

  plusEq:
    (numbersGen: (r: any) => number[]) =>
    (data: GenData): QEq => {
      const arr = numbersGen(data.random);
      const index = data.random.next(arr.length);
      const list = arr.map((n, i) => (i === index ? 'x' : `${n}`));
      return {
        text: `${list.join(' + ')} = ${arr.reduce((a, b) => a + b, 0)}`,
        result: arr[index],
      };
    },

  minusEq:
    (numbersGen: (r: any) => number[]) =>
    (data: GenData): QEq => {
      const arr = numbersGen(data.random);
      if (arr.length !== 2) throw new Error('MinusEq requires 2 numbers');
      const array = [arr[0] + arr[1], arr[1], arr[0]];
      const index = data.random.next(3);
      const list = array.map((n, i) => (i === index ? 'x' : `${n}`));
      return {
        text: `${list[0]} – ${list[1]} = ${list[2]}`,
        result: array[index],
      };
    },

  multiply:
    (numbersGen: (r: any) => number[]) =>
    (data: GenData): QEq => {
      const nums = numbersGen(data.random);
      return {
        text: `${nums.join(' · ')  } =`,
        result: nums.reduce((a, b) => a * b, 1),
      };
    },

  divide:
    (numbersGen: (r: any) => number[]) =>
    (data: GenData): QEq => {
      const arr = numbersGen(data.random);
      if (arr.length !== 2) throw new Error('Divide requires 2 numbers');
      return {
        text: `${arr[0] * arr[1]} : ${arr[1]} =`,
        result: arr[0],
      };
    },

  multiplyAddition:
    (numbersGen: (r: any) => number[]) =>
    (data: GenData): QEq => {
      const arr = numbersGen(data.random);
      if (arr.length !== 2) throw new Error('MultiplyAddition requires 2 numbers [count, value]');
      const count = arr[0];
      const number = arr[1];
      return {
        text: Array.from({ length: count }, () => `${number}`).join(' + '),
        result: count * number,
      };
    },

  plusMinus:
    (count = 3, max = 20) =>
    (data: GenData): QEq => {
      while (true) {
        const arr = Array.from({ length: count }, () => 1 + data.random.next(max));
        const signs = Array.from({ length: count - 1 }, () => data.random.next(2));
        let text = `${arr[0]}`;
        let result = arr[0];
        let ok = true;

        for (let i = 1; i < arr.length; i++) {
          if (signs[i - 1] === 0) {
            text += ` + ${arr[i]}`;
            result += arr[i];
          } else {
            text += ` – ${arr[i]}`;
            result -= arr[i];
          }
          if (result < 0 || result > max) ok = false;
        }
        if (!ok) continue;
        return { text: `${text} =`, result };
      }
    },

  string:
    (formatter: (n: number) => QEq, min: number, max: number) =>
    (data: GenData): QEq => {
      const n = min + data.random.next(max - min + 1);
      return formatter(n);
    },

  intToString:
    (callback: (n: number) => string, min: number, max: number) =>
    (data: GenData): QEq => {
      const n = min + data.random.next(max - min + 1);
      return { text: callback(n), result: n };
    },
};

export class NameInfo {
  constructor(
    public text: string,
    public sex: string
  ) {}
  toString(): string {
    return this.text;
  }
  v(verb: string, fVerb?: string): string {
    if (fVerb) return this.sex === 'f' ? fVerb : verb;
    return verb + (this.sex === 'f' ? 'a' : '');
  }
  x(verb: string): string {
    return this.sex === 'f' ? verb.replace('x', 'a') : verb.replace('x', '');
  }

  static get(): NameInfo[] {
    return NAMES_DATA.map((n) => new NameInfo(n.text, n.sex));
  }
}

export class ThingInfo {
  constructor(
    public n1: string,
    public n234: string,
    public n5p: string
  ) {}
  c(amount: number): string {
    return (
      `${amount} ${  amount === 1 ? this.n1 : amount >= 2 && amount <= 4 ? this.n234 : this.n5p}`
    );
  }
}

export class UnitClass {
  litru(amount: number): string {
    return this.any(amount, 'litr', 'litry', 'litrů');
  }
  korun(amount: number): string {
    return this.any(amount, 'koruna', 'koruny', 'korun');
  }
  korunu(amount: number): string {
    return this.any(amount, 'korunu', 'koruny', 'korun');
  }
  hodinu(amount: number): string {
    return this.any(amount, 'hodinu', 'hodiny', 'hodin');
  }
  minutu(amount: number): string {
    return this.any(amount, 'minutu', 'minuty', 'minut');
  }
  any(amount: number, n1: string, n234: string, n5p: string): string {
    return `${amount} ${  amount === 1 ? n1 : amount >= 2 && amount <= 4 ? n234 : n5p}`;
  }
}

export abstract class WordTaskGenBase implements IGenEq {
  abstract name: string;
  protected generators: ((random: any) => any)[] = [];
  protected names: NameInfo[] = NameInfo.get();
  unit = new UnitClass();

  register(f: (random: any) => any) {
    this.generators.push(f);
  }

  generate(data: GenData): QEq {
    const generator = this.generators[data.random.next(this.generators.length)];
    const p = generator(data.random);
    const list = [];
    if (p.text) list.push(p.text);
    if (p.question) list.push(p.question);
    return {
      text: list.join(' ').trim(),
      result: p.result,
    };
  }

  cap(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  item(random: any, ...items: string[]): string {
    return items[random.next(items.length)];
  }
  integer(random: any, min: number, max: number): number {
    return min + random.next(max - min + 1);
  }
  integers(
    random: any,
    count: number,
    min: number,
    max: number,
    condition: (arr: number[]) => boolean
  ): number[] {
    while (true) {
      const arr = Array.from({ length: count }, () => min + random.next(max - min + 1));
      if (condition(arr)) return arr;
    }
  }
  namesPick(random: any, count: number): NameInfo[] {
    return [...this.names].sort(() => random.next() - 0.5).slice(0, count);
  }
  thing(random: any, ...things: ThingInfo[]): ThingInfo {
    return things[random.next(things.length)];
  }
}

export interface UtilConversion { unitFrom: string[]; times: number; unitTo: string[] }
export interface UtilConversionOpts { correct: UtilConversion; wrong: UtilConversion[] }

export const UnitUtil = {
  decImperialLength: (): UtilConversion[] => [
    { unitFrom: ['stopa', 'stopy', 'stop'], times: 12, unitTo: ['palec', 'palce', 'palců'] },
    { unitFrom: ['yard', 'yary', 'yardů'], times: 36, unitTo: ['palec', 'palce', 'palců'] },
    { unitFrom: ['yard', 'yary', 'yardů'], times: 3, unitTo: ['stopa', 'stopy', 'stop'] },
    { unitFrom: ['míle', 'míle', 'mil'], times: 63360, unitTo: ['palec', 'palce', 'palců'] },
    { unitFrom: ['míle', 'míle', 'mil'], times: 5280, unitTo: ['stopa', 'stopy', 'stop'] },
    { unitFrom: ['míle', 'míle', 'mil'], times: 1760, unitTo: ['yard', 'yary', 'yardů'] },
    { unitFrom: ['palec', 'palce', 'palců'], times: 2.54, unitTo: ['cm'] },
    { unitFrom: ['stopa', 'stopy', 'stop'], times: 30.48, unitTo: ['cm'] },
    { unitFrom: ['yard', 'yary', 'yardů'], times: 0.9144, unitTo: ['m'] },
    { unitFrom: ['míle', 'míle', 'mil'], times: 1.609344, unitTo: ['km'] },
  ],
  decImperialLengthShort: (): UtilConversion[] => [
    { unitFrom: ['ft'], times: 12, unitTo: ['in'] },
    { unitFrom: ['yd'], times: 36, unitTo: ['in'] },
    { unitFrom: ['yd'], times: 3, unitTo: ['ft'] },
    { unitFrom: ['mi'], times: 63360, unitTo: ['in'] },
    { unitFrom: ['mi'], times: 5280, unitTo: ['ft'] },
    { unitFrom: ['mi'], times: 1760, unitTo: ['yd'] },
    { unitFrom: ['in'], times: 2.54, unitTo: ['cm'] },
    { unitFrom: ['ft'], times: 30.48, unitTo: ['cm'] },
    { unitFrom: ['yd'], times: 0.9144, unitTo: ['m'] },
    { unitFrom: ['mi'], times: 1.609344, unitTo: ['km'] },
  ],
  decMetricTime: (): UtilConversion[] => [
    { unitFrom: ['minuta', 'minuty', 'minut'], times: 60, unitTo: ['s'] },
    {
      unitFrom: ['hodina', 'hodiny', 'hodin'],
      times: 3600,
      unitTo: ['sekunda', 'sekundy', 'sekund'],
    },
    { unitFrom: ['hodina', 'hodiny', 'hodin'], times: 60, unitTo: ['minuta', 'minuty', 'minut'] },
    { unitFrom: ['den', 'dny', 'dnů'], times: 24, unitTo: ['hodina', 'hodiny', 'hodin'] },
    { unitFrom: ['týden', 'týdny', 'týdnů'], times: 7, unitTo: ['den', 'dny', 'dnů'] },
    { unitFrom: ['týden', 'týdny', 'týdnů'], times: 168, unitTo: ['hodina', 'hodiny', 'hodin'] },
    { unitFrom: ['sekunda', 'sekundy', 'sekund'], times: 1000, unitTo: ['ms'] },
    { unitFrom: ['rok', 'roky', 'roků'], times: 365.26, unitTo: ['den', 'dny', 'dnů'] },
  ],
  decMetricTimeShort: (): UtilConversion[] => [
    { unitFrom: ['m'], times: 60, unitTo: ['s'] },
    { unitFrom: ['h'], times: 3600, unitTo: ['s'] },
    { unitFrom: ['h'], times: 60, unitTo: ['m'] },
    { unitFrom: ['d'], times: 24, unitTo: ['h'] },
    { unitFrom: ['w'], times: 7, unitTo: ['d'] },
    { unitFrom: ['w'], times: 168, unitTo: ['h'] },
    { unitFrom: ['s'], times: 1000, unitTo: ['ms'] },
    { unitFrom: ['y'], times: 365.26, unitTo: ['d'] },
  ],
  decImperialVolume: (): UtilConversion[] => [
    { unitFrom: ['cup'], times: 8, unitTo: ['fl oz'] },
    { unitFrom: ['pt'], times: 2, unitTo: ['cup'] },
    { unitFrom: ['qt'], times: 2, unitTo: ['pt'] },
    { unitFrom: ['gal'], times: 4, unitTo: ['qt'] },
    { unitFrom: ['pt'], times: 16, unitTo: ['fl oz'] },
    { unitFrom: ['qt'], times: 32, unitTo: ['fl oz'] },
    { unitFrom: ['gal'], times: 128, unitTo: ['fl oz'] },
    { unitFrom: ['fl oz'], times: 29.5735, unitTo: ['ml'] },
    { unitFrom: ['cup'], times: 236.588, unitTo: ['ml'] },
    { unitFrom: ['pt'], times: 473.176, unitTo: ['ml'] },
    { unitFrom: ['qt'], times: 0.946353, unitTo: ['l'] },
    { unitFrom: ['gal'], times: 3.78541, unitTo: ['l'] },
  ],
  decImperialMass: (): UtilConversion[] => [
    { unitFrom: ['oz'], times: 16, unitTo: ['dwt'] },
    { unitFrom: ['lb'], times: 16, unitTo: ['oz'] },
    { unitFrom: ['st'], times: 14, unitTo: ['lb'] },
    { unitFrom: ['cwt'], times: 100, unitTo: ['lb'] },
    { unitFrom: ['ton'], times: 2000, unitTo: ['lb'] },
    { unitFrom: ['oz'], times: 28.3495, unitTo: ['g'] },
    { unitFrom: ['lb'], times: 0.45359237, unitTo: ['kg'] },
    { unitFrom: ['st'], times: 6.35029, unitTo: ['kg'] },
    { unitFrom: ['cwt'], times: 45.3592, unitTo: ['kg'] },
    { unitFrom: ['ton'], times: 907.1847, unitTo: ['kg'] },
  ],
  logLength: (): [number, string][] => [
    [-3, 'mm'],
    [-2, 'cm'],
    [-1, 'dm'],
    [0, 'm'],
    [3, 'km'],
  ],
  logSurface: (): [number, string][] => [
    [-6, 'mm²'],
    [-4, 'cm²'],
    [-2, 'dm²'],
    [0, 'm²'],
    [2, 'a'],
    [4, 'ha'],
    [6, 'km²'],
  ],
  logVolume: (): [number, string][] => [
    [-9, 'mm³'],
    [-6, 'cm³'],
    [-3, 'dm³'],
    [-3, 'l'],
    [-6, 'ml'],
    [0, 'm³'],
  ],
  logWeight: (): [number, string][] => [
    [-6, 'μg'],
    [-3, 'mg'],
    [0, 'g'],
    [1, 'dag'],
    [3, 'kg'],
    [6, 't'],
  ],
  logSiPrefixes: (unit: string): [number, string][] => [
    [12, `T${unit}`],
    [9, `G${unit}`],
    [6, `M${unit}`],
    [3, `k${unit}`],
    [0, unit],
    [-3, `m${unit}`],
    [-6, `μ${unit}`],
    [-9, `n${unit}`],
  ],

  coeff_1: (r: any) => 1,
  coeff_n: (r: any) => 1 + r.next(10),
  coeff_n_n: (r: any) => (1 + r.next(100)) / 10,

  power10: (exponent: number): number => 10**exponent,

  format: (value: number, formats: string[], space = ' '): string => {
    if (formats.length === 0) return value.toString();
    let unit = formats[0];
    if (formats.length === 3) {
      unit = value === 1 ? formats[0] : value >= 2 && value <= 4 ? formats[1] : formats[2];
    } else if (formats.length === 2) {
      unit = value === 1 ? formats[0] : formats[1];
    }
    return `${value}${space}${unit}`;
  },

  getRandomOpts: (r: any, items: [number, string][]): UtilConversionOpts => {
    const arr = items;
    const i1 = r.next(arr.length);
    let i2: number;
    do {
      i2 = r.next(arr.length);
    } while (i1 === i2);
    const diff = arr[i1][0] - arr[i2][0];
    const correct: UtilConversion = {
      unitFrom: [arr[i1][1]],
      times: UnitUtil.power10(diff),
      unitTo: [arr[i2][1]],
    };
    const wrong: UtilConversion[] = arr
      .filter((_, idx) => idx !== i1 && idx !== i2)
      .map((item) => ({
        unitFrom: [arr[i1][1]],
        times: UnitUtil.power10(arr[i1][0] - item[0]),
        unitTo: [item[1]],
      }));
    return { correct, wrong };
  },

  getRandomConversion: (r: any, items: UtilConversion[] | [number, string][]): UtilConversion => {
    if (Array.isArray((items as any)[0])) {
      const arr = items as [number, string][];
      const i1 = r.next(arr.length);
      let i2;
      do {
        i2 = r.next(arr.length);
      } while (i1 === i2);
      return {
        unitFrom: [arr[i1][1]],
        times: UnitUtil.power10(arr[i1][0] - arr[i2][0]),
        unitTo: [arr[i2][1]],
      };
    }
    const arr = items as UtilConversion[];
    return arr[r.next(arr.length)];
  },
};

export class ConversionBaseSingle implements IGenSingle {
  constructor(
    private conversion: (r: any) => UtilConversion,
    private addQuestion: boolean,
    private coeficient: (r: any) => number,
    public name: string
  ) {}
  generate(genData: GenData): QSingle {
    const r = genData.random;
    const gen = this.conversion(r);
    const m = this.coeficient(r);
    let q = UnitUtil.format(m, gen.unitFrom);
    if (this.addQuestion) q += ` = ? ${  gen.unitTo[gen.unitTo.length - 1]}`;
    return {
      texts: [q],
      answer: UnitUtil.format(gen.times * m, gen.unitTo),
    };
  }
}

export class ConversionBaseAbc implements IGenMulti {
  name: string;
  constructor(
    private conversion: (r: any) => UtilConversionOpts,
    private coeficient: (r: any) => number,
    name: string
  ) {
    this.name = name;
  }
  generate(genData: GenData): QMulti {
    const r = genData.random;
    const gen = this.conversion(r);
    const m = this.coeficient(r);
    const q = UnitUtil.format(m, gen.correct.unitFrom);
    const a = UnitUtil.format(gen.correct.times * m, gen.correct.unitTo);
    const list = gen.wrong
      .map((i) => UnitUtil.format(gen.correct.times * m, i.unitTo))
      .slice(0, 2)
      .concat([a])
      .sort(() => r.next() - 0.5);
    return {
      texts: [q],
      answers: list,
      answerIndex: list.indexOf(a),
    };
  }
}

export class ConversionArrayAbc implements IGenMulti {
  name: string;
  constructor(
    private numbers: number[],
    private fromStr: (n: number) => string,
    private toStr: (n: number) => string,
    name: string
  ) {
    this.name = name;
  }
  generate(genData: GenData): QMulti {
    const r = genData.random;
    const n = this.numbers[r.next(this.numbers.length)];
    const items = this.numbers
      .filter((i) => i !== n)
      .sort(() => r.next() - 0.5)
      .slice(0, 2)
      .concat([n])
      .sort(() => r.next() - 0.5);
    const answers = items.map(this.toStr);
    return {
      texts: [this.fromStr(n)],
      answers,
      answerIndex: items.indexOf(n),
    };
  }
}
